import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import {
  useHistory, useLocation, useParams, useRouteMatch, Switch, Route, Redirect,
} from 'react-router-dom';

import { usePostHog } from 'posthog-js/react';

import { AccountIntent, ActivationProvider, useActivation } from './ActivationContext';
import { AuthMethodSelection } from './steps/AuthMethodSelection';
import { OtpVerification } from './steps/OtpVerification';
import { CoverageForm } from './steps/CoverageForm';
import { ActivationComplete } from './steps/ActivationComplete';
import { EmailCallback } from './steps/EmailCallback';
import { EmailEntry } from './steps/EmailEntry';
import { EmailSent } from './steps/EmailSent';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { buildHandoffParams, isAllowedAppHandoffTarget } from '@/utils/urlHelpers';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { REDIRECT_TO_SESSION_KEY, HANDOFF_NONCE_PARAM } from '@/utils/constants';
import {
  CLIENT_PARAM,
  CLIENT_SESSION_KEY,
  isKnownClient,
} from '@/utils/clientRegistry';
import { usePostHogDistinctId } from '@/hooks/usePostHogDistinctId';
import { SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';

interface RouteParams {
  lob: string;
  // eslint-disable-next-line camelcase
  org_slug: string;
}

const ActivationRouterInner: React.FC = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const [sessionChecked, setSessionChecked] = useState(false);
  const posthog = usePostHog();
  const { distinctId: partnerDistinctId } = usePostHogDistinctId();

  // Stitch a partner-originated anonymous PostHog session into this CAP session.
  // When a landing page passes ph_token or ph_distinct_id in the URL, adopt that
  // identity so all activation events land on the same person as the pre-CAP funnel.
  useEffect(() => {
    if (partnerDistinctId) {
      posthog?.identify(partnerDistinctId);
    }
  }, [partnerDistinctId, posthog]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirectTo');
    // The handoff check, not the scheme check. This is the other write to the key that
    // usePostAuthRedirectTo reads, and that reader honours only handoff targets — so a looser
    // value here just puts junk in a key the Spark flows share. No security consequence
    // either way, since the read re-validates; but two writers disagreeing about what belongs
    // in one key is the kind of thing that later reads as permission to relax the reader.
    if (redirectTo && isAllowedAppHandoffTarget(redirectTo)) {
      dispatch({ type: 'SET_REDIRECT_TO', redirectTo });
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }
    // The client name, saved for the same reason and read in preference to the address. Only
    // a name we recognise is stored, so an unknown value cannot sit in storage looking
    // authoritative. Guarded because a browser with storage locked down throws on access and
    // this app has no error boundary — losing the saved copy costs one of the sources, while
    // an unguarded throw blanks the screen mid-flow.
    const client = params.get(CLIENT_PARAM);
    if (client && isKnownClient(client)) {
      try {
        sessionStorage.setItem(CLIENT_SESSION_KEY, client);
      } catch {
        // Nothing to do; the URL and the legacy address remain as sources.
      }
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        if (session) {
          dispatch({ type: 'SET_SESSION', session });
          // All three handoff values, not just the address. This effect has no delay and
          // runs a plain getSession() check, so for a session that already exists — the
          // ordinary case right after a magic-link click — it wins the race against
          // EmailCallback's own deliberately-delayed check and is what actually performs
          // this navigation. buildRedirectParam here dropped client and the flat nonce on
          // every one of those hops, unconditionally, regardless of which check won.
          const searchParams = new URLSearchParams(location.search);
          const redirectParam = buildHandoffParams(
            searchParams.get('redirectTo'),
            searchParams.get(CLIENT_PARAM),
            searchParams.get(HANDOFF_NONCE_PARAM),
          );
          if (state.accountIntent === AccountIntent.Signin) {
            // A fresh magic-link login also lands here with a session already set —
            // don't count that as a "resumed" session.
            if (!location.pathname.includes('/email/callback')) {
              posthog?.capture('LoginSessionResumed', { program: SLEEPIO_CANOPY_PROGRAM });
            }
            dispatch({ type: 'SET_STEP', step: 'activation_complete' });
            history.replace(`${url}/complete${redirectParam}`);
          } else {
            try {
              await activateSleepioConfirm({ accessToken: session.access_token });
              dispatch({ type: 'SET_STEP', step: 'coverage_form' });
              history.replace(`${url}/coverage${redirectParam}`);
            } catch {
              await getSupabaseClient().auth.signOut();
              history.replace(`${url}/auth${redirectParam}`);
            }
          }
        }
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []); // eslint-disable-line

  if (!sessionChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const fallbackSearchParams = new URLSearchParams(location.search);
  const authFallbackUrl = `${url}/auth${buildHandoffParams(
    fallbackSearchParams.get('redirectTo'),
    fallbackSearchParams.get(CLIENT_PARAM),
    fallbackSearchParams.get(HANDOFF_NONCE_PARAM),
  )}`;

  return (
    <Switch>
      <Route exact path={`${path}/auth`} component={AuthMethodSelection} />
      <Route exact path={`${path}/otp`} component={OtpVerification} />
      <Route exact path={`${path}/email`} component={EmailEntry} />
      <Route exact path={`${path}/email/callback`} component={EmailCallback} />
      <Route exact path={`${path}/sent`} component={EmailSent} />
      <Route exact path={`${path}/coverage`} component={CoverageForm} />
      <Route exact path={`${path}/complete`} component={ActivationComplete} />
      <Redirect to={authFallbackUrl} />
    </Switch>
  );
};

const ActivationRouter: React.FC = () => {
  // eslint-disable-next-line camelcase
  const { org_slug: orgSlug } = useParams<RouteParams>();
  const { pathname } = useLocation();
  const accountIntent = pathname.includes('/sign-up') ? AccountIntent.Create : AccountIntent.Signin;

  return (
    <ActivationProvider initialOrgSlug={orgSlug} initialAccountIntent={accountIntent}>
      <ActivationRouterInner />
    </ActivationProvider>
  );
};

export default ActivationRouter;
